import { getBrandBlogs } from '@api/blogs';
import { getBestSeller, getBrand, getBrandCategories } from '@api/brand';
import { addFeedback } from '@api/feedback';
import { getFeaturedProducts } from '@api/product';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs
} from '@chakra-ui/react';
import { Container, Header, Section } from '@components';
import Blog from '@components/cards/blog';
import { BrandProductCard } from '@components/cards/product/brand';
import Filters from '@components/catalogControls/filters';
import MobileControls from '@components/catalogControls/mobile-controls';
import Sorting from '@components/catalogControls/sorting';
import { CarouselContainer } from '@components/container/carousel';
import { Footer } from '@components/footer';
import {
    TBrand,
    TFeedback,
    TIdentity,
    TProduct,
    TProductCategory,
    TSocialAccount,
} from '@components/types';
import appConfig from '@core/appconfig';
import {
    getSafeUrl,
    humanizeNumber,
    requestLogin,
    sanityIoImageLoader,
} from '@core/utils';
import { isLoggedIn } from '@recoil/atoms/profile';
import { PostOrPage } from '@tryghost/content-api';
import { Input, message } from 'antd';
import { useInView } from 'framer-motion';
import { Loader, Share2 } from 'lucide-react';
import moment from 'moment';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { SocialIcon } from 'react-social-icons';
import { RWebShare } from 'react-web-share';
import { useRecoilValue } from 'recoil';
import { SwiperSlide } from 'swiper/react';

const SORTING_OPTIONS = [
    { value: 'relevence', label: 'Relevence' },
    { value: 'bestseller', label: 'Bestseller' },
    { value: 'popularity', label: 'Popularity' },
    { value: 'rating', label: 'Rating' },
];

interface Props {
    initialBrand?: TBrand;
    initialProductList?: TProduct[];
    initialBrandCategories?: TProductCategory[];
    initialBestsellerList?: TProduct[];
    initialAboutBrand?: PostOrPage | null;
}

export const getServerSideProps: GetServerSideProps<Props> = async (
    context
) => {
    const { slug } = context.params;
    const brandId = parseInt(slug[0] as string);
    const [
        initialBrand,
        initialProductList,
        initialBrandCategories,
        initialBestsellerList,
    ] = await Promise.all([
        getBrand(brandId),
        getFeaturedProducts(null, null, brandId, SORTING_OPTIONS[0].value),
        getBrandCategories(brandId),
        getBestSeller(brandId),
    ]);

    // const aboutBrandRes = await getBrandBlogs(1, 1, [
    //     initialBrand?.name?.replace("'", "")?.split(' ')?.join('-'),
    // ])

    try {
        const aboutBrandRes = await getBrandBlogs(1, 1, [
            initialBrand?.name?.replace("'", "")?.split(' ')?.join('-'),
        ]);

        return {
            props: {
                initialBrand,
                initialProductList,
                initialBrandCategories,
                initialBestsellerList,
                initialAboutBrand:
                    aboutBrandRes?.posts?.length > 0 ? aboutBrandRes?.posts[0] : null,
            },
        };
    } catch (error) {
        console.error("Error fetching brand blogs:", error);
        return {
            props: {
                initialBrand,
                initialProductList,
                initialBrandCategories,
                initialBestsellerList,
                initialAboutBrand: null,
            },
        };
    }
};

const Navbar = dynamic(
    () => import('../../src/components/navbar').then((mod) => mod.Navbar),
    {
        ssr: false,
    }
);

const BrandPage: React.FC<Props> = ({
    initialBrand,
    initialProductList,
    initialBrandCategories,
    initialBestsellerList,
    initialAboutBrand,
}) => {
    const router = useRouter();
    const isLogin = useRecoilValue(isLoggedIn);
    const [userInfo, setUserInfo] = useState < TIdentity > (
        typeof window !== 'undefined' && JSON.parse(localStorage.getItem('profile'))
    );
    const [feedback, setFeedback] = useState < TFeedback > ();
    const [submitted, setSubmitted] = useState < boolean > (false);
    const [brand, setBrand] = useState < TBrand > (initialBrand);
    const [products, setProducts] = useState < TProduct[] > (initialProductList);
    const [pageNumber, setPageNumber] = useState(0);
    const [isLoadMore, setIsLoadMore] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);
    const [bestSellerProducts, setBestSellerProducts] = useState < TProduct[] > (
        initialBestsellerList
    );
    const [brandCategories, setBrandCategories] = useState < TProductCategory[] > (
        initialBrandCategories
    );
    const [minimumCategories, setMinimumCategories] = useState <
        TProductCategory[]
        > ([]);
    const [categoryId, setCategoryId] = useState < number > ();
    const [orderingOn, setOrderingOn] = useState < null | string > (
        SORTING_OPTIONS[0].value
    );
    const [isData, setIsData] = useState(true);
    const [skip, setSkip] = useState(0);
    const pageSize = 10;

    const [aboutBrand, setAboutBrand] = useState < PostOrPage | null > (
        initialAboutBrand
    );

    const [blogs, setBlogs] = useState < PostOrPage[] > ([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMoreBlogs, setHasMoreBlogs] = useState(true);
    const PAGE_SIZE = 10;

    const ref = useRef(null);
    const isInView = useInView(ref);

    async function fetchBlogs(brand: TBrand) {
        if (!hasMoreBlogs || isLoading) return;
        setIsLoading(true);
        const pageNo = blogs.length / PAGE_SIZE + 1;
        getBrandBlogs(pageNo, PAGE_SIZE, [brand?.name?.replace("'", "")?.split(' ')?.join('-')])
            .then((response) => {
                if (pageNo == 1) {
                    setBlogs(
                        response?.posts?.filter(
                            (blog: PostOrPage) =>
                                blog?.tags?.find((tag) => tag?.name == 'ABOUT') === undefined
                        )
                    );
                } else {
                    setBlogs([
                        ...blogs,
                        ...response?.posts?.filter(
                            (blog: PostOrPage) =>
                                blog?.tags?.find((tag) => tag?.name === 'ABOUT') === undefined
                        ),
                    ]);
                }
                setHasMoreBlogs(response?.meta?.pagination?.pages > pageNo);
            })
            .catch(() => {
                setHasMoreBlogs(false);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const handleTabsChange = (index: number) => {
        setTabIndex(index);
    };

    const getAllBestSeller = (id: number) => {
        getBestSeller(id).then((res) => {
            setBestSellerProducts(res);
        });
    };

    const getAllBrandCategories = (id: number) => {
        getBrandCategories(id).then((res) => {
            setBrandCategories(res);
            setMinimumCategories(res.slice(0, 10));
        });
    };

    useEffect(() => {
        let id = (
            router.query.slug === undefined ? router.query.slug : router.query.slug[0]
        ) as string;
        if (!router.isReady || id == brand?.id.toString()) {
            return;
        }
        const res: Promise<TBrand> = getBrand(parseInt(id));
        res.then((brand) => {
            setBrand(brand);
            setFeedback({
                page_url: router.pathname,
                brand_id: brand.id,
                first_name: userInfo?.first_name,
                last_name: userInfo?.last_name,
            });
        });
    }, [router.isReady, router.query.slug]);

    const handleInput = async (name: string, value: any) => {
        setFeedback({ ...feedback, [name]: value });
    };

    const submitFeedback = async () => {
        if (
            feedback?.feedback === '' ||
            feedback?.feedback === undefined ||
            feedback?.feedback === null
        ) {
            message.error("Message shouldn't be empty.");
            return;
        }
        addFeedback(feedback)
            .then((res) => {
                message.success('Message Submitted');
                setSubmitted(true);
            })
            .catch((e: any) => {
                message.error(e?.response?.data?.detail || 'Some Error Occured');
                if (e.response?.status === 401) {
                    requestLogin(router.asPath);
                }
            });
    };

    useEffect(() => {
        getAllBestSeller(brand?.id);
        getAllBrandCategories(brand?.id);
    }, [brand]);

    function handleAboutBrand() {
        getBrandBlogs(1, 1, [brand?.name?.replace("'", "")?.split(' ')?.join('-'), 'ABOUT']).then(
            (res) => {
                if (res?.posts?.length > 0) {
                    setAboutBrand(res?.posts[0]);
                }
            }
        );
    }
    useEffect(() => {
        handleAboutBrand();
    }, [brand]);

    useEffect(() => {
        fetchBlogs(brand);
    }, [brand, isInView, hasMoreBlogs, isLoading]);

    function handleCategoryChange(id: number) {
        if (categoryId === id) setCategoryId(null);
        else setCategoryId(id);
        setProducts([]);
        setIsData(true);
        setSkip(0);
    }

    function handleSortingChange(option: string) {
        if (orderingOn === option) setOrderingOn(null);
        else setOrderingOn(option);
        setIsData(true);
        setSkip(0);
    }

    function handleClearFilters() {
        setCategoryId(null);
        setOrderingOn(null);
        setProducts([]);
        setIsData(true);
        setSkip(0);
    }

    async function fetchBrandProducts() {
        setIsLoading(true);
        getFeaturedProducts(
            categoryId > 0 ? categoryId : null,
            null,
            brand?.id,
            orderingOn,
            skip / pageSize,
            pageSize
        ).then((newProducts) => {
            if (skip === 0) setProducts(newProducts);
            else {
                if (newProducts.length) {
                    setProducts([...products, ...newProducts]);
                }
                else {
                    setIsData(false);
                }
            }
        })
            .finally(() => {
                setIsLoading(false);
            });
    }

    useEffect(() => {
        if (isData) {
            fetchBrandProducts();
        }
    }, [brand, categoryId, orderingOn, skip]);

    useEffect(() => {
        if (isInView && isData && isLoading === false) {
            setSkip((prevSkip) => prevSkip + pageSize);
            setIsLoading(true);
        }
    }, [isInView, isData, isLoading]);

    return (
        <Container>
            <Head>
                <title>{`${brand?.name} Products - ${appConfig?.appName}`}</title>
                <meta
                    name="Keywords"
                    content={`${brand?.name},Local Products,Local Services,Buy ${brand?.name},`}
                />
                <meta
                    name="Description"
                    content={`Get ${brand?.name} products from nearby local stores on ${appConfig.appName}`}
                />
                <link
                    rel="canonical"
                    href={`https://jhattse.com/brand/${brand?.id}/${getSafeUrl(
                        brand?.name
                    )}`}
                />
                <meta
                    property="og:title"
                    content={`${brand?.name} products buy and explore`}
                />
                <meta
                    name="og:description"
                    content={`Get ${brand?.name} products from nearby local stores`}
                />
                <meta name="og:image" content={`${brand.image}`} />
                <meta
                    property="og:url"
                    content={`https://jhattse.com/brand/${brand?.id}/${getSafeUrl(
                        brand?.name
                    )}`}
                />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(
                            {
                                '@context': 'https://schema.org',
                                '@type': 'OnlineStore',
                                image: brand?.image,
                                logo: brand?.logo,
                                name: brand?.name,
                                sameAs: (brand?.social_accounts || []).map(
                                    (sa: TSocialAccount) => {
                                        return sa.link;
                                    }
                                ),
                                description: brand?.description,
                                url: `https://jhattse.com/brand/${brand?.id}/${getSafeUrl(
                                    brand?.name
                                )}`,
                                foundingDate: brand?.info?.established,
                            },
                            (key, value) => {
                                // Check if key matches the string "pin" or "mob"
                                // if matched return value "undefined"
                                if (key === 'foundingDate') {
                                    return brand?.info?.established?.length > 0
                                        ? value
                                        : undefined;
                                }
                                if (key === 'logo') {
                                    return brand?.logo != undefined && brand?.logo != null
                                        ? value
                                        : undefined;
                                }
                                if (key === 'image') {
                                    return brand?.image != undefined && brand?.image != null
                                        ? value
                                        : undefined;
                                }
                                return value;
                            }
                        ),
                    }}
                />
            </Head>
            <Header />
            <div className="flex flex-grow max-w-[1240px] mx-auto w-full">
                <div className="w-full font-manrope">
                    <div className="hidden py-8 px-2 md:flex items-center text-base">
                        <Breadcrumb className="text-base text-neutral-400 font-medium font-manrope list-none text-breadcrumbs">
                            <BreadcrumbItem>
                                <BreadcrumbLink href="/">Home</BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbItem>
                                <BreadcrumbLink href="/brand/explore">Brand</BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbItem
                                className="text-ellipsis text-left break-words line-clamp-1 select-none text-neutral-800 font-semibold"
                                isCurrentPage
                            >
                                <BreadcrumbLink
                                    href={`/brand/${brand?.id}/${getSafeUrl(brand?.name)}`}
                                >
                                    {brand?.name.length > 80
                                        ? `${brand?.name.slice(0, 80)}...`
                                        : brand?.name}
                                </BreadcrumbLink>
                                <ol className="p-0 list-none"></ol>
                            </BreadcrumbItem>
                        </Breadcrumb>
                    </div>
                    {/* Brand Banner */}
                    <div className="p-2">
                        <Image
                            loader={sanityIoImageLoader}
                            src={brand?.image || 'https://jhattse.com/assets/noimage.png'}
                            alt={brand?.name}
                            width="1000"
                            height="400"
                            quality={100}
                            className="w-full h-full max-h-96 lt-sm:max-h-40 object-cover rounded-lg"
                        />
                    </div>
                    <div className="flex justify-between items-center px-2">
                        <div className="py-2 flex gap-2 items-center">
                            <Image
                                loader={sanityIoImageLoader}
                                priority={true}
                                src={brand?.logo || brand?.image || 'https://jhattse.com/assets/noimage.png'}
                                alt={brand?.name}
                                width="50"
                                height="50"
                                quality={100}
                                className="object-cover rounded"
                            />
                            <h1 className="text-neutral-900 font-bold text-xl lt-sm:text-base">
                                {brand?.name}
                            </h1>
                        </div>
                        <div className="flex items-center justify-end lt-sm:justify-start cursor-pointer">
                            <RWebShare
                                data={{
                                    text: `${brand?.name} is on ${appConfig?.appName}`,
                                    url: `/brand/${brand?.id}/${getSafeUrl(brand?.name)}`,
                                    title: 'brand',
                                }}
                                onClick={() => console.debug('shared successfully!')}
                            >
                                <button className="flex gap-1 items-center justify-center text-brand-500 font-bold text-base cursor-pointer">
                                    <Share2 fontWeight={700} />{' '}
                                    <span className="lt-sm:hidden">SHARE</span>
                                </button>
                            </RWebShare>
                        </div>
                    </div>
                    {/* Tabs */}
                    <div className="h-4"></div>
                    <div className="flex flex-col gap-4 flex-grow px-2">
                        <Tabs index={tabIndex} onChange={handleTabsChange}>
                            <TabList className="gap-8 border-b border-b-solid border-b-neutral-400 overflow-x-scroll no-scrollbar">
                                <Tab
                                    _selected={{
                                        borderBottom: '4px solid #ED7829',
                                        fontWeight: '700',
                                    }}
                                    className="font-manrope text-neutral-800 font-medium md:text-base text-sm py-2"
                                >
                                    Our Products
                                </Tab>
                                <Tab
                                    _selected={{
                                        borderBottom: '4px solid #ED7829',
                                        fontWeight: '700',
                                    }}
                                    className="font-manrope text-neutral-800 font-medium md:text-base text-sm py-2"
                                >
                                    About Us
                                </Tab>
                                <Tab
                                    _selected={{
                                        borderBottom: '4px solid #ED7829',
                                        fontWeight: '700',
                                    }}
                                    className="font-manrope text-neutral-800 font-medium md:text-base text-sm py-2"
                                >
                                    Our Performance
                                </Tab>
                                <Tab
                                    _selected={{
                                        borderBottom: '4px solid #ED7829',
                                        fontWeight: '700',
                                    }}
                                    className="font-manrope text-neutral-800 font-medium md:text-base text-sm py-2"
                                >
                                    Updates
                                </Tab>
                            </TabList>
                            <TabPanels>
                                <TabPanel className="py-4">
                                    <div className="flex flex-col gap-4">
                                        {(brandCategories?.length || 0) > 0 && (
                                            <div>
                                                <h4 className="md:text-lg text-neutral-900 font-semibold py-2 text-base">
                                                    Our Categories
                                                </h4>
                                                {/* Brand Category in Large Screen */}
                                                <div className="md:flex flex-row gap-4 pt-2 hidden">
                                                    {brandCategories?.map((item) => {
                                                        return (
                                                            <Link
                                                                href={`/category/${item?.id}/${getSafeUrl(
                                                                    item?.name
                                                                )}`}
                                                                prefetch={false}
                                                            >
                                                                <div className="relative rounded-lg flex justify-center items-center border border-solid border-neutral-300 w-full max-w-60 max-h-60 h-40">
                                                                    <Image
                                                                        loader={sanityIoImageLoader}
                                                                        src={
                                                                            item?.image ||
                                                                            'https://jhattse.com/assets/noimage.png'
                                                                        }
                                                                        alt={item?.name}
                                                                        width="150"
                                                                        height="150"
                                                                        className="w-full max-w-60 max-h-60 h-40 object-cover rounded-lg"
                                                                    />
                                                                    <span className="absolute text-neutral-50 font-semibold text-lg bottom-0 left-0  bg-gradient-to-b from-transparent via-black/50 to-black w-full md:h-20 h-16 flex items-center rounded-lg">
                                                                        <p className="md:text-base text-sm px-4">{item?.name}</p>
                                                                    </span>
                                                                </div>
                                                            </Link>
                                                        );
                                                    })}
                                                </div>

                                                {/* Brand Category in screens*/}
                                                <div className="pt-2 md:hidden block">
                                                    <CarouselContainer>
                                                        {brandCategories?.map((item) => {
                                                            return (
                                                                <SwiperSlide
                                                                    style={{ width: 'auto' }}
                                                                    className="min-w-0"
                                                                >
                                                                    <Link
                                                                        href={`/category/${item?.id}/${getSafeUrl(
                                                                            item?.name
                                                                        )}`}
                                                                        prefetch={false}
                                                                    >
                                                                        <div className="relative rounded-lg flex justify-center items-center border border-solid border-neutral-300">
                                                                            <Image
                                                                                loader={sanityIoImageLoader}
                                                                                src={
                                                                                    item?.image ||
                                                                                    'https://jhattse.com/assets/noimage.png'
                                                                                }
                                                                                alt={item?.name}
                                                                                width="150"
                                                                                height="150"
                                                                                className="w-full max-h-60 object-cover rounded-lg"
                                                                            />
                                                                            <span className="absolute text-neutral-50 font-semibold text-lg bottom-0 left-0  bg-gradient-to-b from-transparent via-black/50 to-black w-full md:h-20 h-16 flex items-center rounded-lg">
                                                                                <p className="md:text-base text-sm px-4">{item?.name}</p>
                                                                            </span>
                                                                        </div>
                                                                    </Link>
                                                                </SwiperSlide>
                                                            );
                                                        })}
                                                    </CarouselContainer>
                                                </div>
                                            </div>
                                        )}
                                        <Section
                                            title="Bestsellers"
                                            products={bestSellerProducts}
                                            element={BrandProductCard}
                                            isCarousel={true}
                                            style="mt-2 pb-2"
                                            headStyle="md:text-lg py-1 font-semibold text-base"
                                        />
                                        <div className="w-full h-fit md:py-10 lg:py-0 grid grid-cols-1 md:grid-cols-4">
                                            <Filters
                                                categoryId={categoryId}
                                                handleCategoryChange={handleCategoryChange}
                                                handleClearFilters={handleClearFilters}
                                                allCategories={brandCategories}
                                                minimumCategories={minimumCategories}
                                            />

                                            <div className="w-full md:col-span-3 pb-4">
                                                <Sorting
                                                    options={SORTING_OPTIONS}
                                                    orderingOn={orderingOn}
                                                    handleSortingChange={handleSortingChange}
                                                />
                                                {/* to control sorting and filtering on smaller devices */}
                                                {products?.length > 0 &&
                                                    <>
                                                        <MobileControls
                                                            filterByCategory={false}
                                                            sortingOptions={SORTING_OPTIONS}
                                                            handleSortingChange={handleSortingChange}
                                                        />
                                                        <div className="md:px-6 xl:px-12 py-2 grid grid-cols-2 lg:grid-cols-4 gap-4">
                                                            {products?.map((product) => (
                                                                <BrandProductCard
                                                                    key={product.id}
                                                                    product={product}
                                                                />
                                                            ))}
                                                        </div>
                                                    </>}
                                                {isLoading && (
                                                    <div className="px-12 py-6 flex justify-center">
                                                        <Loader className="w-8 h-8 text-neutral-400 animate-spin" />
                                                    </div>
                                                )}
                                            </div>
                                            <div ref={ref} className="w-full h-10"></div>
                                        </div>
                                    </div>
                                </TabPanel>

                                {/* About Section */}
                                <TabPanel className="py-4">
                                    <div className="w-full">
                                        {aboutBrand ? (
                                            <>
                                                <div className="flex flex-col items-center mx-auto md:mt-8 w-full max-w-[720px]">
                                                    <div>
                                                        <figure>
                                                            {aboutBrand?.feature_image && (
                                                                <Image
                                                                    src={aboutBrand.feature_image}
                                                                    alt={
                                                                        aboutBrand?.feature_image_alt
                                                                            ? aboutBrand?.feature_image_alt
                                                                            : 'blog feature image'
                                                                    }
                                                                    width={1000}
                                                                    height={400}
                                                                    className="lt-sm:w-full"
                                                                />
                                                            )}
                                                            {aboutBrand?.feature_image_caption && (
                                                                <figcaption
                                                                    className="text-center text-sm text-neutral-500 pt-2"
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: aboutBrand?.feature_image_caption,
                                                                    }}
                                                                ></figcaption>
                                                            )}
                                                        </figure>
                                                    </div>
                                                    <div className=" max-w-[720px]">
                                                        <article className="prose prose-stone max-w-none">
                                                            <div
                                                                dangerouslySetInnerHTML={{
                                                                    __html: aboutBrand?.html || '',
                                                                }}
                                                            />
                                                        </article>
                                                    </div>

                                                    <div className="post-meta text-sm text-neutral-500 uppercase pt-2">
                                                        <span className="post-meta-item post-meta-date">
                                                            <time dateTime={aboutBrand?.published_at}>
                                                                LAST UPDATED{' '}
                                                                {moment(aboutBrand?.updated_at).format(
                                                                    'DD MMM YYYY'
                                                                )}
                                                            </time>
                                                        </span>
                                                        {aboutBrand?.tags.map((tag) => {
                                                            return (
                                                                <span className="post-meta-item post-meta-tags">
                                                                    <span className="post-tag font-semibold">
                                                                        {tag.name}
                                                                    </span>
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="flex flex-col items-center mx-auto w-full max-w-[720px]">
                                                <span className="text-base text-neutral-800 font-manrope font-medium">
                                                    Brand details not available but products are.
                                                </span>
                                                <span className="font-manrope text-brand font-semibold">Happy Shopping!</span>
                                            </div>
                                        )}
                                    </div>
                                </TabPanel>
                                {/* Our Performance Section */}
                                <TabPanel className="py-4">
                                    <div className="pt-4">
                                        <div className="grid grid-cols-4 gap-4 lt-sm:grid-cols-2 py-2">
                                            {(brand?.info || {})?.products && (
                                                <div className="bg-neutral-50 p-8 lt-sm:p-2 rounded-lg flex flex-col gap-2">
                                                    <h4 className="text-neutral-900 font-bold text-2xl lt-sm:text-xl">
                                                        {(brand?.info || {})?.products}
                                                    </h4>
                                                    <p className="text-neutral-900 font-medium text-lg lt-sm:text-base">
                                                        Listed Products
                                                    </p>
                                                </div>
                                            )}
                                            {(brand?.info || {})?.customers && (
                                                <div className="bg-neutral-50 p-8 lt-sm:p-2 rounded-lg flex flex-col gap-2">
                                                    <h4 className="text-neutral-900 font-bold text-2xl lt-sm:text-xl">
                                                        {(brand?.info || {})?.customers}
                                                    </h4>
                                                    <p className="text-neutral-900 font-medium text-lg lt-sm:text-base">
                                                        Satisfied Customers
                                                    </p>
                                                </div>
                                            )}
                                            {(brand?.info || {})?.sales && (
                                                <div className="bg-neutral-50 p-8 lt-sm:p-2 rounded-lg flex flex-col gap-2">
                                                    <h4 className="text-neutral-900 font-bold text-2xl lt-sm:text-xl">
                                                        {(brand?.info || {})?.sales}
                                                    </h4>
                                                    <p className="text-neutral-900 font-medium text-lg lt-sm:text-base">
                                                        Products Sold
                                                    </p>
                                                </div>
                                            )}
                                            {(brand?.info || {})?.established && (
                                                <div className="bg-neutral-50 p-8 lt-sm:p-2 rounded-lg flex flex-col gap-2">
                                                    <h4 className="text-neutral-900 font-bold text-2xl lt-sm:text-xl">
                                                        {moment((brand?.info || {})?.established).format("MMM YYYY")}
                                                    </h4>
                                                    <p className="text-neutral-900 font-medium text-lg lt-sm:text-base">
                                                        Established
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                        <div className="h-4"></div>
                                        <div className="grid grid-cols-2 gap-2 lt-sm:grid-cols-1 py-2">
                                            <div className="bg-neutral-50 p-8 lt-sm:p-2 rounded-lg flex flex-col gap-2">
                                                <h4 className="text-neutral-900 font-bold text-lg lt-sm:text-sm">
                                                    Interest Meter
                                                </h4>
                                                <p>
                                                    {(brand?.info || {})?.interest > 0 ? (
                                                        <span className="text-4xl lt-sm:text-2xl font-bold text-custom_cyan tracking-wider">
                                                            {(brand?.info || {})?.interest}/100
                                                        </span>
                                                    ) : (
                                                        <span className="text-2xl font-bold text-custom_cyan tracking-wider">
                                                            Not Available Yet
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                            <div className="md:p-8 p-2 pb-16 flex flex-col gap-4">
                                                {brand?.social_accounts.length !== 0 &&
                                                    brand?.social_accounts?.map((item) => {
                                                        return (
                                                            <span className="select-none">
                                                                <SocialIcon url={item?.link} />{' '}
                                                                {`${humanizeNumber(item.followers)} ${item.followers < 2 ? 'follower' : 'followers'
                                                                    }`}
                                                            </span>
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    </div>
                                </TabPanel>
                                <TabPanel>
                                    <div className="py-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
                                        {blogs && blogs.length > 0 ? (
                                            blogs?.map((blog: PostOrPage) => (
                                                <Blog
                                                    key={blog.id}
                                                    blog={blog}
                                                    readMoreURL={`/brand/blogs/${blog.slug}?brand_id=${brand.id}`}
                                                />
                                            ))
                                        ) : (
                                            <span className="text-base text-neutral-800 font-medium">
                                                No blogs found. Check back later
                                            </span>
                                        )}

                                        {isLoading && hasMoreBlogs && (
                                            <div className="px-12 py-6 flex justify-center">
                                                <Loader className="w-8 h-8 text-neutral-400 animate-spin" />
                                            </div>
                                        )}

                                        <div ref={ref} className="h-20" />
                                    </div>
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    </div>
                </div>
            </div>
            {
                tabIndex == 1 && (
                    <div className="bg-brand-100 p-4 md:px-20 pb-16">
                        {!submitted ? (
                            <>
                                <h2 className="font-manrope font-semibold text-lg text-neutral-800 select-none">
                                    Get In Touch
                                </h2>
                                <div className="grid grid-cols-2 gap-8 justify-between lt-sm:flex flex-col py-2">
                                    <div className="flex flex-col gap-4">
                                        {!isLogin ? (
                                            <>
                                                <div className="flex flex-row gap-2">
                                                    <div className="inline-block w-1/2 pr-1">
                                                        <label className="block font-semibold text-sm text-neutral-700">
                                                            First name
                                                        </label>
                                                        <Input
                                                            className="w-full p-1 text-neutral-700 bg-neutral-100 border border-solid border-neutral-300 rounded-md"
                                                            value={feedback?.first_name}
                                                            onChange={(evt) =>
                                                                handleInput(evt.target.name, evt.target.value)
                                                            }
                                                            id="first_name"
                                                            name="first_name"
                                                            type="text"
                                                            placeholder="First name"
                                                            autoComplete="given-name"
                                                            required
                                                        />
                                                    </div>
                                                    <div className="inline-block w-1/2 pr-1">
                                                        <label className=" block font-semibold text-sm text-neutral-700">
                                                            Last name
                                                        </label>
                                                        <Input
                                                            className="w-full p-1 text-neutral-700 bg-neutral-100 border border border-solid border-neutral-300 rounded-md"
                                                            value={feedback?.last_name}
                                                            onChange={(evt) =>
                                                                handleInput(evt.target.name, evt.target.value)
                                                            }
                                                            id="last_name"
                                                            name="last_name"
                                                            type="text"
                                                            placeholder="Last name"
                                                            autoComplete="family-name"
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <></>
                                        )}
                                        <Input
                                            className="bg-neutral-50 p-2 text-neutral-600 focus:outline-none focus:ring focus:border-brand border border-solid border-neutral-300 rounded-md"
                                            placeholder="Your Email Address"
                                            name="email"
                                            autoComplete="email"
                                            value={feedback?.email || userInfo?.email}
                                            onChange={(evt) =>
                                                handleInput(evt.target.name, evt.target.value)
                                            }
                                        />
                                        <textarea
                                            className="bg-neutral-50 p-2 text-neutral-600 focus:outline-none focus:ring focus:border-brand border border-solid border-neutral-300 rounded-md"
                                            placeholder="Your Message upto 500 chars"
                                            rows={3}
                                            maxLength={500}
                                            name="feedback"
                                            autoComplete="new-password"
                                            value={feedback?.feedback}
                                            onChange={(evt) =>
                                                handleInput(evt.target.name, evt.target.value)
                                            }
                                        />
                                        <div>
                                            <button
                                                className="text-neutral-50 font-medium font-manrope text-base py-1 px-4 bg-brand-500 rounded-md"
                                                onClick={() => {
                                                    submitFeedback();
                                                }}
                                            >
                                                Submit
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-2 lt-sm:grid grid-cols-2">
                                        {brand?.social_accounts.length !== 0 &&
                                            brand?.social_accounts?.map((item: TSocialAccount) => {
                                                return (
                                                    <div className="flex gap-4 items-center">
                                                        <span className="select-none">
                                                            <SocialIcon url={item?.link} />{' '}
                                                            {`${humanizeNumber(item.followers)}${item.followers < 2 ? ' follower' : '+ followers'
                                                                }`}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="flex-row justify-center items-center p-4">
                                <div>
                                    <p className="text-success-400 font-bold">
                                        Message submitted successfully!
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )
            }
            <Navbar />
            <Footer />
        </Container >
    );
};

export default BrandPage;
